import React, { useEffect } from "react";

import {
  MdAccountBox,
  MdLock,
  MdLockOpen,
  MdOutlineNavigateBefore,
  MdSettings,
} from "react-icons/md";
import PageTitle from "../../components/PageTitle/PageTitle";
import TwoSidebars from "../../layouts/TwoSidebars/TwoSidebars";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import apiClient from "../../helpers/api-client";
import {
  API_ENDPOINT_UPDATE_PROFILE,
  BUTTON_STYLES,
  ICON_SIZES,
} from "../../helpers/constants";
import { showNotification } from "../../helpers/utils";
import SettingsBlocks from "../../components/SettingsBlocks/SettingsBlocks";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import { CONFIRM_DIALOG_ICON_SIZE } from "../../constants/icons";
import useCurrentUser from "../../hooks/useCurrentUser";
import useMobile from "../../hooks/useMobile";
import useGeneralOptionsDropdownMenu from "../../hooks/useGeneralOptionsDropdownMenu";
import CollapsibleBlock from "src/components/CollapsibleBlock/CollapsibleBlock";
import TextList from "src/components/TextList/TextList";
import { AnimatePresence } from "framer-motion";
import { EButtonKind } from "src/components/Button/ButtonTypes";

const VoiceAssistantsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const { voiceAssistants, resetCache } = useCurrentUser();

  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const { t } = useTranslation();
  const [alexaIsDisabled, setAlexaIsDisabled] = useState<boolean>(
    !!voiceAssistants?.alexa?.disabled
  );

  const generalOptionsDropdown = useGeneralOptionsDropdownMenu();

  useEffect(() => {
    setAlexaIsDisabled(voiceAssistants?.alexa?.disabled);
  }, [voiceAssistants]);

  const updateVoiceAssistants = async () => {
    setIsUpdating(true);

    const formData = new FormData();

    formData.append(
      "voiceAssistants",
      JSON.stringify({
        ...voiceAssistants,
        alexa: {
          disabled: !alexaIsDisabled,
        },
      })
    );

    await apiClient
      .put(API_ENDPOINT_UPDATE_PROFILE, formData, {
        // headers: { "Content-Type": "multipart/form-data" },
      })
      .then(async (res: any) => {
        await resetCache();
        showNotification({
          kind: "success",
          message: "Configuración actualizada correctamente",
        });
        setShowConfirmDialog(false);
      })
      .catch((e) => {
        showNotification({
          message: "Error actualizando la configuración",
          kind: "error",
        });
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  // const clientId =
  // "amzn1.application-oa2-client.2d98d2a38fab4d45adf55ee4886fb5c4"; // Tu client_id
  // const redirectUri = encodeURIComponent(
  // "https://ui.expenser.net/services/link-with-alexa"
  // ); // La URL de redirección, debe coincidir con la configurada en Amazon
  // const scope = "alexa::profile"; // Los scopes que necesitas, por ejemplo 'profile', 'email', etc.

  // Construir la URL para el inicio de sesión de Amazon
  // const authUrl = `https://www.amazon.com/ap/oa?client_id=${clientId}&scope=${scope}&response_type=code&redirect_uri=${redirectUri}`;

  return (
    <>
      <TwoSidebars
        right={
          isMobile ? null : (
            <SettingsBlocks layout="vertical" columns={isMobile ? 2 : 1} />
          )
        }
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "gap-2",
            "md:gap-2",
            "h-full",
          ])}
        >
          <AnimatePresence>
            <>
              <PageTitle
                titleLink="/settings"
                icon={<MdSettings size={ICON_SIZES.MD} />}
                title={t("settings.title")}
                subtitle={t("Asistentes de voz")}
                isLoading={false}
                rightSide={
                  <>
                    <ButtonGroup
                      align="right"
                      gap={1}
                      buttons={[
                        {
                          icon: (
                            <MdOutlineNavigateBefore size={ICON_SIZES.SM} />
                          ),
                          className: isMobile ? BUTTON_STYLES.HEADING : "",
                          tooltip: "Atras",
                          onClick: () => {
                            navigate("/settings");
                          },
                        },
                        generalOptionsDropdown,
                      ]}
                    />
                  </>
                }
              />

              <CollapsibleBlock
                title="Alexa"
                buttons={[
                  {
                    onClick: () => {
                      setShowConfirmDialog(true);
                    },
                    text: voiceAssistants?.alexa?.disabled
                      ? "Desbloquear"
                      : "Bloquear",
                    kind: voiceAssistants?.alexa?.disabled
                      ? EButtonKind.SAVE
                      : EButtonKind.DANGER,
                    icon: voiceAssistants?.alexa?.disabled ? (
                      <MdLockOpen size={ICON_SIZES.SM} />
                    ) : (
                      <MdLock size={ICON_SIZES.SM} />
                    ),
                  },
                ]}
              >
                <div
                  className={clsx([
                    "flex",
                    "flex-col",
                    "gap-2",
                    "md:gap-4",
                    "text-sm",
                    "md:text-base",
                  ])}
                >
                  {voiceAssistants?.alexa?.disabled ? (
                    <div>
                      El acceso a eXpenser.net en tus dispositivos Alexa está
                      deshabilitado.
                    </div>
                  ) : (
                    <div>
                      El acceso a eXpenser.net está habilitado para todos tus
                      dispositivos Alexa.
                    </div>
                  )}
                  <div>
                    Toma en cuenta que las funcionalidades del skill dependerán
                    de si estás utilizando un dispositivo con pantalla o sin
                    pantalla.
                  </div>
                </div>
              </CollapsibleBlock>
              <CollapsibleBlock title="¿Qué puedes preguntarle a Alexa?">
                <div className={clsx(["flex", "w-full", "flex-col", "gap-6"])}>
                  <TextList
                    items={[
                      { text: "Abre mi banco" },
                      { text: "¿Cuánto he gastado hoy?" },
                      { text: "¿Cuánto he ganado hoy?" },
                      { text: "¿Cuánto he gastado este mes?" },
                      { text: "¿Cuánto he ganado este mes?" },
                      { text: "Muestra mis cuentas" },
                      { text: "¿Cuáles son mis últimas transacciones?" },
                      { text: "¿Cuánto tengo disponible?" },
                      { text: "Cierra mi banco" },
                    ]}
                  >
                    <TextList.Top>
                      <div className="text-sm md:text-base">
                        Habilita el skill de eXpenser.net en Alexa y accede a
                        tus datos por medio de tu asistente de voz.
                      </div>
                    </TextList.Top>
                  </TextList>
                </div>
              </CollapsibleBlock>
            </>
          </AnimatePresence>
        </div>
      </TwoSidebars>
      {showConfirmDialog && (
        <ConfirmDialog
          icon={<MdAccountBox size={CONFIRM_DIALOG_ICON_SIZE} />}
          title={t("Assistentes de voz")}
          description={t("¿Deseas actualizar la configuración?")}
          onClose={() => {
            setShowConfirmDialog(false);
          }}
          confirmKind={EButtonKind.SAVE}
          onConfirm={() => {
            updateVoiceAssistants();
          }}
          isLoading={isUpdating}
        />
      )}
    </>
  );
};

export default VoiceAssistantsPage;
